import React, { useEffect, useRef } from 'react';
import { InfoWindow } from '@react-google-maps/api';

const DriverInfoWindow = ({ 
    driverObj, 
    position, 
    assignedRoute, 
    assignedJob, 
    onClose, 
    showInfoCard,
    setShowInfoCard 
}) => {
    const infoTimeoutRef = useRef(null);

    // Auto-hide logic (5 seconds)
    useEffect(() => {
        if (showInfoCard) {
            if (infoTimeoutRef.current) clearTimeout(infoTimeoutRef.current);
            infoTimeoutRef.current = setTimeout(() => {
                setShowInfoCard(false);
            }, 5000);
        }
        return () => {
            if (infoTimeoutRef.current) clearTimeout(infoTimeoutRef.current);
        };
    }, [showInfoCard, setShowInfoCard]);

    if (!showInfoCard || !driverObj) return null;

    return (
        <InfoWindow
            position={position}
            onCloseClick={onClose}
            options={{ pixelOffset: new window.google.maps.Size(0, -10) }}
        >
            {/* 
              Tailwind styling applied directly. 
              Note: The parent Google Maps popup bubble might still have a white border natively, 
              but the contents will beautifully adapt to dark/light themes. 
            */}
            <div className="min-w-[200px] font-sans p-1">
                <h4 className="m-0 mb-1.5 text-[15px] font-black text-slate-900 dark:text-slate-100 uppercase tracking-wide">
                    {driverObj.name}
                </h4>
                <p className="m-0 mb-1 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 font-medium">
                    <svg className="w-3 h-3 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                    {driverObj.phone || 'N/A'}
                </p>
                
                {driverObj.speed > 0 && (
                    <p className="m-0 mb-2 text-[11px] font-bold text-teal-700 dark:text-teal-400 flex items-center gap-1">
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"/><circle cx="12" cy="12" r="3"/></svg>
                        {driverObj.speed} km/h
                    </p>
                )}

                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg px-2.5 py-2 mb-2 border border-slate-200 dark:border-slate-700">
                    <p className="m-0 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        {assignedRoute ? 'ASSIGNED ROUTE' : (assignedJob ? 'ASSIGNED JOB' : 'ASSIGNMENT')}
                    </p>
                    <p className={`m-0 mt-0.5 text-[13px] font-bold ${(assignedRoute || assignedJob) ? 'text-teal-700' : 'text-rose-500'} flex items-center`}>
                        {assignedRoute ? (
                            <><svg className="w-4 h-4 mr-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" /></svg> {assignedRoute.name}</>
                        ) : (assignedJob ? (
                            <><svg className="w-4 h-4 mr-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg> {assignedJob.name}</>
                        ) : (
                            <><svg className="w-4 h-4 mr-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg> No Task Assigned</>
                        ))}
                    </p>
                    
                    {assignedRoute && (
                        <p className="m-0 mt-1 text-[11px] font-semibold text-slate-500 flex items-center">
                            {assignedRoute.type === 'morning' ? (
                                <><svg className="w-3 h-3 mr-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 3v2m0 14v2m9-9h-2M5 12H3m14.243-7.071l-1.414 1.414M7.172 18.364l-1.414 1.414M18.364 18.364l-1.414-1.414M7.172 7.172L5.757 5.757M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg> Morning Shift</>
                            ) : (
                                <><svg className="w-3 h-3 mr-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg> Afternoon Shift</>
                            )}
                            <span className="mx-1">•</span>
                            {(assignedRoute.children || []).length} students
                        </p>
                    )}
                    
                    {assignedJob && (
                        <p className="m-0 mt-1 text-[11px] font-semibold text-slate-500 flex items-center">
                            <svg className="w-3 h-3 mr-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            {assignedJob.scheduledDate ? new Date(assignedJob.scheduledDate).toLocaleDateString() : ''} {assignedJob.scheduledTime || ''}
                            <span className="mx-1">•</span>
                            {assignedJob.status}
                        </p>
                    )}
                    
                    {/* Premium Route Legend */}
                    {(assignedRoute || assignedJob) && (
                        <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-slate-200 dark:border-slate-700/50">
                            <div className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></span>
                                <span className="text-[9px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Start</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]"></span>
                                <span className="text-[9px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">End</span>
                            </div>
                        </div>
                    )}
                </div>

                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${driverObj.isVerified ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                    {driverObj.isVerified ? (
                        <><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg> Verified</>
                    ) : (
                        <><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Unverified</>
                    )}
                </span>
            </div>
        </InfoWindow>
    );
};

export default DriverInfoWindow;
